import { OrderStatus } from '@/compiled_proto/com/celertech/orderrouting/api/enums/OrderStatusProto';
import { OrderBlotterRow } from '@/components/modules/Blotters/OrderBlotterTable';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';
import { MdChevronRight, MdLink } from 'react-icons/md';

// OrderBlotterRow | OrderHistory
export const orderStatusColorCell = ({ row }: CellContext<any, any>) => {
    const val = row.original.status as keyof typeof OrderStatus;

    if (val === 'NEW') return 'text-brand-green';
    else if (['PART_FILLED', 'FILLED', 'TRADE'].includes(val)) return 'text-brand-green';
    else if (val.includes('PENDING')) return 'text-brand-text';
    else if (['CANCELED', 'REJECTED'].includes(val)) return 'text-brand-red';
    else return 'text-brand-text';
};

export const ordStateColorCell = ({ row }: CellContext<any, any>) => {
    const val = row.original.status as keyof typeof OrderStatus;

    // RESTATEMENT = 8,
    // TRADE_CORRECT = 10,
    // TRADE_CANCEL = 11,
    // TRADE_RELEASED_CLEARING = 12,
    // EXPIRED = 13,
    // SUSPENDED = 14,
    // REPLACED = 16,
    // SENDING = 17,
    // UNRECOGNIZED = -1

    const workingList = ['NEW', 'PART_FILLED', 'PENDING_NEW', 'PENDING_AMEND'];
    const completedList = ['FILLED'];
    const cancelledList = ['CANCELED'];
    const rejectedList = ['REJECTED'];

    const supplementaryWorkingList: (keyof typeof OrderStatus)[] = ['PENDING_CANCEL'];

    if ([...workingList, ...supplementaryWorkingList].includes(val))
        return {
            className: 'text-yellow-500',
            title: 'WORKING'
        };
    else if (completedList.includes(val))
        return {
            className: 'text-brand-green',
            title: 'COMPLETED'
        };
    else if (cancelledList.includes(val)) {
        if (row.original.cumQty === 0) {
            return {
                className: 'text-brand-red',
                title: 'REJECTED'
            };
        } else {
            return {
                className: 'text-brand-green',
                title: 'COMPLETED'
            };
        }
    } else if (rejectedList.includes(val))
        return {
            className: 'text-brand-red',
            title: 'REJECTED'
        };
    else
        return {
            className: '',
            title: val
        };
};

export const orderColumns = (lang: UseLanguageReturn, generalSettings): ColumnDef<OrderBlotterRow>[] => {
    return [
        {
            accessorKey: 'orderId',
            header: lang.blotters.orderId,
            size: 160,
            cell: ({ row, getValue }: CellContext<OrderBlotterRow, any>) => {
                const canExpand = row.getCanExpand();

                return (
                    <div
                        className="flex items-center gap-1"
                        style={{
                            paddingLeft: `${row.depth * 0}rem`
                        }}>
                        <div className={`flex space-x-1 truncate`}>
                            {canExpand && (
                                <button title={row.getIsExpanded() ? 'Collapse current row' : 'Expand current row'}>
                                    <MdChevronRight
                                        className={cn('h-3.5 w-3.5 text-primary duration-200', {
                                            ['rotate-90']: row.getIsExpanded()
                                        })}
                                        aria-hidden
                                    />
                                </button>
                            )}

                            {!canExpand && row.original.parentStrategyID && (
                                <MdLink className={cn('h-3.5 w-3.5 text-primary duration-200 rotate-90')} aria-hidden />
                            )}

                            <span className="truncate">{getValue()}</span>
                        </div>
                    </div>
                );
            }
        },
        {
            accessorKey: 'status',
            header: lang.blotters.ordState,
            size: 100,
            cell: (context: CellContext<OrderBlotterRow, any>) => context.getValue()
        },
        { accessorKey: 'pair', header: lang.blotters.instrument, size: 100 },
        { accessorKey: 'orderType', header: lang.blotters.orderType, size: 80 },
        {
            accessorKey: 'side',
            header: lang.blotters.side,
            size: 60,
            cell: ({ row, getValue }: CellContext<OrderBlotterRow, any>) => {
                const value = getValue();
                return (
                    <div
                        className={cn('', {
                            'text-brand-green': value === 'BUY',
                            'text-brand-red': value === 'SELL'
                        })}>
                        {value}
                    </div>
                );
            }
        },
        { accessorKey: 'qty', header: lang.blotters.ordQty, size: 80 },
        { accessorKey: 'cumQty', header: lang.blotters.filledQty, size: 80 },
        { accessorKey: 'price', header: lang.blotters.price, size: 80 },
        { accessorKey: 'stopPx', header: lang.blotters.stopPrice, size: 80 },
        { accessorKey: 'lastPrice', header: lang.blotters.lastPrice, size: 80 },
        { accessorKey: 'lastQty', header: lang.blotters.lastQty, size: 80 },
        { accessorKey: 'account', header: lang.blotters.account, size: 100 },
        { accessorKey: 'tif', header: lang.blotters.tif, size: 60 }
    ];
};
